/**
 * Resolves *which* debug namespaces are active, and at what level.
 *
 * Two independent switches, because they solve two different problems:
 *
 * 1. `NEXT_PUBLIC_DEBUG` / `NEXT_PUBLIC_DEBUG_LEVEL` — the build/deploy default.
 *    NEXT_PUBLIC_* values are inlined by `next build`, so changing these needs a
 *    rebuild. Use them to ship an environment (e.g. the netcore stage stack)
 *    with verbosity on by default.
 *
 * 2. A per-browser runtime override — `?debug=<spec>` in the URL, or
 *    `window.roomsterDebug.enable(<spec>)`, persisted in localStorage. This is
 *    what you reach for when a bug is already in front of you on a deployed
 *    build: no rebuild, no redeploy, and it only affects your own browser.
 *    Gated by `NEXT_PUBLIC_DEBUG_RUNTIME`, which is unset in production — see
 *    `isRuntimeOverrideAllowed()` below.
 *
 * Spec syntax follows the `debug` npm package: comma/space separated globs, `*`
 * wildcard, `-` prefix to exclude. Examples:
 *
 *   *                    everything
 *   listing:*            every listing namespace
 *   request,form         requests and form validation
 *   *,-request           everything except requests
 */

export const DEBUG_LEVELS = [
  'error',
  'warn',
  'info',
  'debug',
  'trace',
] as const;

export type DebugLevel = (typeof DEBUG_LEVELS)[number];

export const DEBUG_STORAGE_KEY = 'roomster:debug';
export const DEBUG_LEVEL_STORAGE_KEY = 'roomster:debug-level';
export const DEBUG_QUERY_PARAM = 'debug';

const DEFAULT_LEVEL: DebugLevel = 'debug';

const LEVEL_RANK: Record<DebugLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  trace: 4,
};

const isBrowser = () => globalThis.window !== undefined;

function isDebugLevel(value: string | null | undefined): value is DebugLevel {
  return !!value && (DEBUG_LEVELS as readonly string[]).includes(value);
}

/**
 * Compiles a spec into a namespace predicate. Exported for tests.
 */
export function compileDebugSpec(spec: string): (namespace: string) => boolean {
  const includes: RegExp[] = [];
  const excludes: RegExp[] = [];

  for (const pattern of spec.split(/[\s,]+/).filter(Boolean)) {
    const isExclude = pattern.startsWith('-');
    const body = isExclude ? pattern.slice(1) : pattern;

    if (!body) {
      continue;
    }

    // Escape regex metacharacters, then turn `*` back into `.*`.
    const source = `^${body.replaceAll(/[$()+.?[\\\]^{|}]/g, String.raw`\$&`).replaceAll('*', '.*')}$`;
    (isExclude ? excludes : includes).push(new RegExp(source));
  }

  if (includes.length === 0 && excludes.length === 0) {
    return () => false;
  }

  return (namespace: string) => !excludes.some((pattern) => pattern.test(namespace))
    && includes.some((pattern) => pattern.test(namespace));
}

function readStorage(key: string): string | null {
  if (!isBrowser()) {
    return null;
  }

  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    // Private mode / storage disabled — fall back to the build-time default.
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  if (!isBrowser()) {
    return;
  }

  try {
    if (value === null) {
      globalThis.localStorage.removeItem(key);
    } else {
      globalThis.localStorage.setItem(key, value);
    }
  } catch {
    // Ignore — the override is best-effort.
  }
}

/**
 * On the server, `DEBUG` (not inlined, so it can be changed with a container
 * restart alone) takes precedence over the baked-in `NEXT_PUBLIC_DEBUG`.
 */
function getBuildTimeSpec(): string {
  if (isBrowser()) {
    return process.env.NEXT_PUBLIC_DEBUG ?? '';
  }

  return process.env.DEBUG ?? process.env.NEXT_PUBLIC_DEBUG ?? '';
}

function getBuildTimeLevel(): DebugLevel {
  const raw = isBrowser()
    ? process.env.NEXT_PUBLIC_DEBUG_LEVEL
    : process.env.DEBUG_LEVEL ?? process.env.NEXT_PUBLIC_DEBUG_LEVEL;

  return isDebugLevel(raw) ? raw : DEFAULT_LEVEL;
}

/**
 * The runtime override is cached in memory.
 *
 * This matters for production, where debug is off: `isNamespaceEnabled` runs on
 * every single debug call (three per HTTP request in `libs/request`), and
 * `localStorage.getItem` is a synchronous main-thread read. Resolving it from
 * storage each time would put real work in a hot path for users who will never
 * see a line of output.
 *
 * Invalidated by the enable/disable/reset helpers (same tab) and by the
 * `storage` event (another tab), so a toggle still takes effect immediately.
 */
interface DebugOverride {
  level: string | null;
  spec: string | null;
}

/** Shared constant: the server has no runtime override, and this is read per call. */
const NO_OVERRIDE: DebugOverride = {
  level: null,
  spec: null,
};

let cachedOverride: DebugOverride | null = null;
let isStorageListenerAttached = false;

function attachStorageListener(): void {
  if (isStorageListenerAttached || !isBrowser()) {
    return;
  }

  isStorageListenerAttached = true;

  try {
    globalThis.addEventListener('storage', (event) => {
      // A null key means storage was cleared wholesale, which also drops the
      // override — treat it the same as an explicit change.
      if (event.key === null || event.key === DEBUG_STORAGE_KEY || event.key === DEBUG_LEVEL_STORAGE_KEY) {
        cachedOverride = null;
      }
    });
  } catch {
    // Not fatal — the same-tab helpers still invalidate the cache.
  }
}

/**
 * Whether the per-browser runtime override (`?debug=`, `window.roomsterDebug`,
 * localStorage) may be honoured at all.
 *
 * Production ships with this unset, so a spec cannot be turned on from a
 * visitor's browser: the build-time default is the only switch that applies.
 *
 * Deliberately its own flag rather than a `NODE_ENV` check. The netcore stage
 * stack runs with NODE_ENV=production so that `next start` serves the build the
 * client bundle was compiled for, and that stack is exactly where the override
 * has to keep working — keying off NODE_ENV would disable debug precisely where
 * it is wanted.
 *
 * Written as a full static `process.env.NEXT_PUBLIC_*` expression so `next
 * build` inlines it. With the flag blank the whole body folds to a constant:
 * the production bundle compiles this to `function(){return!1}`. The override
 * code itself is still emitted (the call site keeps it reachable), but it can
 * never run — nothing is installed, nothing is parsed, nothing is read.
 */
export function isRuntimeOverrideAllowed(): boolean {
  const flag = process.env.NEXT_PUBLIC_DEBUG_RUNTIME ?? '';

  return flag !== '' && flag !== '0' && flag !== 'false';
}

function getOverride(): DebugOverride {
  // `isBrowser()` is tested first so the server never pays for the flag read:
  // it has no runtime override either way.
  if (!isBrowser() || !isRuntimeOverrideAllowed()) {
    return NO_OVERRIDE;
  }

  if (cachedOverride === null) {
    attachStorageListener();

    cachedOverride = {
      level: readStorage(DEBUG_LEVEL_STORAGE_KEY),
      spec: readStorage(DEBUG_STORAGE_KEY),
    };
  }

  return cachedOverride;
}

/** Drops the in-memory copy so the next read re-resolves from storage. */
function invalidateOverride(): void {
  cachedOverride = null;
}

let cachedSpec: string | null = null;
let cachedMatcher: ((namespace: string) => boolean) | null = null;

function getMatcher(spec: string) {
  if (cachedMatcher === null || cachedSpec !== spec) {
    cachedSpec = spec;
    cachedMatcher = compileDebugSpec(spec);
  }

  return cachedMatcher;
}

export function getDebugSpec(): string {
  return getOverride().spec ?? getBuildTimeSpec();
}

export function getDebugLevel(): DebugLevel {
  const override = getOverride().level;

  return isDebugLevel(override) ? override : getBuildTimeLevel();
}

export function isNamespaceEnabled(namespace: string): boolean {
  const spec = getDebugSpec();

  if (!spec) {
    return false;
  }

  // Every debug call funnels through here, including the ones in libs/request
  // that run on each HTTP call — and the spec is user-supplied (?debug=…,
  // roomsterDebug.enable(…)), so it reaches `new RegExp`. Callers invoke this
  // outside their own try/catch, so a throw here would break the request path
  // itself. Failing closed keeps a bad spec to "no output".
  try {
    return getMatcher(spec)(namespace);
  } catch {
    return false;
  }
}

export function isLevelEnabled(level: DebugLevel): boolean {
  return LEVEL_RANK[level] <= LEVEL_RANK[getDebugLevel()];
}

/** Turns debug on for this browser only, persisted across reloads. */
export function enableDebug(spec = '*', level?: DebugLevel): void {
  writeStorage(DEBUG_STORAGE_KEY, spec);

  if (level) {
    writeStorage(DEBUG_LEVEL_STORAGE_KEY, level);
  }

  invalidateOverride();
}

/** Clears the runtime override, falling back to the build-time default. */
export function disableDebug(): void {
  writeStorage(DEBUG_STORAGE_KEY, '');
  writeStorage(DEBUG_LEVEL_STORAGE_KEY, null);
  invalidateOverride();
}

/** Removes the override entirely (build-time default applies again). */
export function resetDebug(): void {
  writeStorage(DEBUG_STORAGE_KEY, null);
  writeStorage(DEBUG_LEVEL_STORAGE_KEY, null);
  invalidateOverride();
}

/**
 * Applies a `?debug=<spec>&debugLevel=<level>` URL override, if present.
 * Called once on mount by `DebugBootstrap`.
 */
export function applyDebugSearchParams(search: string): boolean {
  if (!isBrowser()) {
    return false;
  }

  const params = new URLSearchParams(search);
  const spec = params.get(DEBUG_QUERY_PARAM);

  if (spec === null) {
    return false;
  }

  const level = params.get('debugLevel');

  if (spec === 'off' || spec === '0' || spec === 'false') {
    disableDebug();
  } else {
    enableDebug(spec === '' || spec === '1' || spec === 'true' ? '*' : spec, isDebugLevel(level) ? level : undefined);
  }

  return true;
}
